/**
 * B1 (founder 14-oct-2026) — «Editar cita» desde la agenda: cliente, servicio/precio/duración,
 * fecha/inicio/fin, barbero (barbería con agenda_access), notas. Guardar solo con cambios.
 * Cobrada ⇒ solo fecha/hora/notas (etiqueta «Horario editado»). Conflicto ⇒ diálogo B3.
 */
import { useEffect, useMemo, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { PenLine, Save, Trash2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { bookingApi } from '@/lib/bookingApi';
import { friendlyError } from '@/lib/apiError';
import { tr } from '@/contexts/LanguageContext';
import { formatDuration } from '@/utils/bookingFormat';
import { ScheduleConflictDialog, isScheduleConflict, conflictRules } from '@/components/Profile/ScheduleConflictDialog';
import { AssignClientPicker } from '@/components/Bookings/AssignClientPicker';

const toMin = (t) => { const [h, m] = String(t || '0:0').split(':').map(Number); return h * 60 + m; };
const toHHMM = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

const Field = ({ label, children, testId }) => (
  <div className="space-y-1" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
    {children}
  </div>
);

export const EditBookingSheet = ({ booking, open, onOpenChange, onSaved, onCancelBooking, manager = false, barbers = null, services: servicesProp = null }) => {
  // Parte 1 (founder 14-oct-2026): pagada completa ⇒ solo fecha/hora/notas · depósito ⇒ servicios editables si total ≥ depósito · sin pagar ⇒ todo.
  const fullyPaid = Boolean(booking) && (booking.payment_status === 'paid' || booking.status === 'completed');
  const depositPaid = Boolean(booking) && booking.payment_status === 'deposit_paid' && !fullyPaid;
  const paid = fullyPaid || depositPaid;
  const amountPaid = Number(booking?.amount_paid || 0);
  const initLines = useMemo(() => {
    if (!booking) return [];
    const src = (booking.services || []).length ? booking.services : [{ id: booking.service_id || null, name: booking.service_name, price: booking.price, duration: toMin(booking.end) - toMin(booking.start) }];
    return src.map((l, i) => ({ key: `${l.id || 'svc'}-${i}`, id: l.id || null, name: l.name, price: Number(l.price || 0), duration: Number(l.duration ?? l.duration_minutes ?? 0) }));
  }, [booking]);
  const init = useMemo(() => booking ? ({
    client_user_id: booking.client_id || null, client_name: booking.client_name || '', client_phone: booking.client_phone || '',
    lines: initLines, service_ids: initLines.map((l) => l.id),
    price: booking.price ?? '', duration: toMin(booking.end) - toMin(booking.start),
    date: booking.date, start: booking.start, end: booking.end, barber_id: booking.barber_id || '', note: booking.note || '',
  }) : null, [booking]);
  const [form, setForm] = useState(init);
  const [services, setServices] = useState(servicesProp || []);
  const [pickClient, setPickClient] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflictRulesList, setConflictRulesList] = useState([]);

  useEffect(() => { setForm(init); setPickClient(false); setConflictOpen(false); }, [init, open]);
  useEffect(() => {
    if (!open || servicesProp || !booking?.barber_id) return;
    bookingApi.services(booking.barber_username || booking.barber_id).then((r) => setServices(Array.isArray(r) ? r : (r?.items || r?.services || []))).catch(() => setServices([]));
  }, [open, servicesProp, booking?.barber_id]);

  if (!booking || !form) return null;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setStart = (v) => set({ start: v, end: toHHMM(Math.min(24 * 60, toMin(v) + form.duration)) });
  const setEnd = (v) => set({ end: v, duration: Math.max(5, toMin(v) - toMin(form.start)) });
  // Parte 1 — líneas editables: cambiar por otro del catálogo, editar precio/duración, quitar (mínimo 1), añadir.
  const applyLines = (lines) => {
    if (!lines.length) return;
    const price = Math.round(lines.reduce((a, l) => a + Number(l.price || 0), 0) * 100) / 100;
    const duration = lines.reduce((a, l) => a + Number(l.duration || 0), 0) || form.duration;
    set({ lines, service_ids: lines.map((l) => l.id), price, duration, end: toHHMM(Math.min(24 * 60, toMin(form.start) + duration)) });
  };
  const catalogLine = (x, key) => ({ key: key || `${x.id}-${Date.now()}`, id: x.id, name: x.name, price: Number(x.price || 0), duration: Number(x.duration_minutes || 0) });
  const addService = (id) => { const x = services.find((v) => v.id === id); if (x && form.lines.length < 6) applyLines([...form.lines, catalogLine(x)]); };
  const removeLine = (key) => { if (form.lines.length > 1) applyLines(form.lines.filter((l) => l.key !== key)); };
  const replaceLine = (key, id) => { const x = services.find((v) => v.id === id); if (x) applyLines(form.lines.map((l) => (l.key === key ? catalogLine(x, key) : l))); };
  const patchLine = (key, patch) => applyLines(form.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const linesChanged = JSON.stringify(form.lines.map((l) => [l.id, l.name, l.price, l.duration])) !== JSON.stringify(init.lines.map((l) => [l.id, l.name, l.price, l.duration]));
  const belowDeposit = depositPaid && linesChanged && Number(form.price) + 0.005 < amountPaid;

  const changes = {};
  if (linesChanged) changes.services = form.lines.map((l) => ({ id: l.id || undefined, name: l.name, price: Number(l.price), duration: Number(l.duration) }));
  if (String(form.price) !== String(init.price) && form.price !== '' && !changes.services) changes.price = Number(form.price);
  if (form.duration !== init.duration && !changes.services) changes.duration_minutes = form.duration;
  if (form.date !== init.date) changes.date = form.date;
  if (form.start !== init.start) changes.start_time = form.start;
  if (form.end !== init.end && form.duration === init.duration) changes.end_time = form.end;
  if (form.barber_id !== init.barber_id && form.barber_id) changes.barber_id = form.barber_id;
  if (form.note !== init.note) changes.note = form.note;
  const dirty = Object.keys(changes).length > 0 && !belowDeposit;

  const save = async (allowOverlap = false) => {
    if (!dirty) return;
    setSaving(true);
    try {
      await bookingApi.editBooking(booking.booking_id, { ...changes, ...(allowOverlap ? { allow_overlap: true } : {}) });
      toast.success(tr("Cita actualizada"));
      setConflictOpen(false);
      onSaved();
    } catch (err) {
      if (isScheduleConflict(err) && !allowOverlap) { setConflictRulesList(conflictRules(err)); setConflictOpen(true); return; }
      setConflictOpen(false);
      toast.error(friendlyError(err, tr("No pudimos guardar los cambios")));
    } finally {
      setSaving(false);
    }
  };

  const lockMoney = fullyPaid;        // servicios/precio: solo bloqueados si está pagada completa
  const lockClient = manager; // 21-oct-2026: asignar cliente no mueve dinero ⇒ permitido también en citas cobradas
  return (<>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="bg-card border-border rounded-t-2xl max-h-[92vh] overflow-y-auto" data-testid="edit-booking-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-lg text-foreground flex items-center gap-2"><PenLine className="w-4 h-4 text-primary" />{tr("Editar cita")}</SheetTitle>
          {fullyPaid && <p className="text-[11px] text-amber-300" data-testid="edit-booking-paid-hint">{tr("Cita ya cobrada: solo puedes cambiar fecha, hora y notas. Quedará marcada como «Horario editado».")}</p>}
          {depositPaid && <p className="text-[11px] text-amber-300" data-testid="edit-booking-deposit-hint">{tr("Depósito pagado: ${0}. Puedes cambiar los servicios mientras el nuevo total no sea menor que el depósito.", amountPaid.toFixed(2))}</p>}
        </SheetHeader>
        <div className="mt-3 space-y-4">
          <Field label={tr("Cliente")} testId="edit-booking-client">
            {/* CRÍTICO 21-oct-2026 — «Asignar cliente»: ficha + cuenta; nunca texto libre (evita fichas duplicadas y citas sin dueño). */}
            {pickClient ? (
              <AssignClientPicker bookingId={booking.booking_id} onClose={() => setPickClient(false)}
                onAssigned={(b) => { set({ client_user_id: b?.client_id || null, client_name: b?.client_name || form.client_name, client_phone: b?.client_phone || '' }); setPickClient(false); onSaved?.(); }} />
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate" data-testid="edit-booking-client-name">{form.client_name || tr("Walk-in")}</p>
                  <p className="text-[11px] text-muted-foreground" data-testid="edit-booking-client-account">{form.client_user_id ? tr("Con cuenta: la cita le aparece en su app") : tr("Sin cuenta vinculada: asígnale un cliente para que le aparezca en su app")}</p>
                </div>
                {!lockClient && <Button size="sm" variant="outline" className="h-8 rounded-full border-border" onClick={() => setPickClient(true)} data-testid="edit-booking-change-client"><Search className="w-3.5 h-3.5 mr-1" />{form.client_user_id ? tr("Cambiar") : tr("Asignar cliente")}</Button>}
              </div>
            )}
          </Field>

          <Field label={tr("Servicios")} testId="edit-booking-service">
            <ul className="space-y-1.5" data-testid="edit-booking-service-lines">
              {form.lines.map((l, idx) => (
                <li key={l.key} className="rounded-lg border border-border bg-background px-3 py-2 text-sm space-y-1.5" data-testid={`edit-booking-service-line-${l.id || idx}`}>
                  <div className="flex items-center gap-2">
                    {lockMoney ? <span className="flex-1 truncate">{l.name}</span> : (
                      <select value={l.id && services.some((x) => x.id === l.id) ? l.id : '__custom'} onChange={(e) => e.target.value !== '__custom' && replaceLine(l.key, e.target.value)}
                        className="flex-1 min-w-0 h-9 rounded-md bg-background border border-border px-2 text-sm" data-testid={`edit-booking-service-select-${l.id || idx}`} aria-label={tr("Cambiar servicio")}>
                        {!(l.id && services.some((x) => x.id === l.id)) && <option value="__custom">{l.name}</option>}
                        {services.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                      </select>
                    )}
                    {lockMoney && <span className="text-xs text-muted-foreground tabular-nums">{formatDuration(l.duration)}</span>}
                    {lockMoney && <span className="font-semibold tabular-nums">${Number(l.price).toFixed(2)}</span>}
                    {!lockMoney && (
                      <button type="button" disabled={form.lines.length <= 1} onClick={() => removeLine(l.key)} className="text-muted-foreground hover:text-destructive disabled:opacity-30" aria-label={tr("Quitar servicio")} data-testid={`edit-booking-service-remove-${l.id || idx}`}><X className="w-4 h-4" /></button>
                    )}
                  </div>
                  {!lockMoney && (
                    <div className="grid grid-cols-2 gap-2">
                      <label className="text-[11px] text-muted-foreground">{tr("Precio")}
                        <Input type="number" inputMode="decimal" min={0} step="0.01" value={l.price} onChange={(e) => patchLine(l.key, { price: e.target.value === '' ? 0 : Number(e.target.value) })} className="bg-background border-border h-9 mt-0.5" data-testid={`edit-booking-line-price-${l.id || idx}`} /></label>
                      <label className="text-[11px] text-muted-foreground">{tr("Duración (min)")}
                        <Input type="number" inputMode="numeric" min={5} max={480} step={5} value={l.duration} onChange={(e) => patchLine(l.key, { duration: Math.max(5, Number(e.target.value) || 5) })} className="bg-background border-border h-9 mt-0.5" data-testid={`edit-booking-line-duration-${l.id || idx}`} /></label>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            {belowDeposit && <p className="text-[11px] text-destructive mt-1" data-testid="edit-booking-below-deposit">{tr("El nuevo total no puede ser menor que el depósito ya pagado (${0})", amountPaid.toFixed(2))}</p>}
            {!lockMoney && services.some((x) => !form.service_ids.includes(x.id)) && (
              <select value="" onChange={(e) => addService(e.target.value)} className="mt-1.5 w-full h-10 rounded-md bg-background border border-border px-3 text-sm text-primary" data-testid="edit-booking-add-service">
                <option value="">+ {tr("Añadir un servicio")}</option>
                {services.filter((x) => !form.service_ids.includes(x.id)).map((x) => <option key={x.id} value={x.id}>{x.name} · ${Number(x.price).toFixed(2)} · {formatDuration(x.duration_minutes)}</option>)}
              </select>
            )}
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <p className="text-[11px] text-muted-foreground mb-1">{tr("Precio")}</p>
                <p className="h-10 flex items-center text-sm font-semibold tabular-nums" data-testid="edit-booking-price-input">${Number(form.price || 0).toFixed(2)}{depositPaid && <span className="ml-2 text-[11px] font-normal text-muted-foreground">{tr("saldo ${0}", Math.max(Number(form.price || 0) - amountPaid, 0).toFixed(2))}</span>}</p>
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground mb-1">{tr("Duración")}</p>
                <p className="h-10 flex items-center text-sm text-muted-foreground" data-testid="edit-booking-duration-locked">{formatDuration(form.duration)}</p>
              </div>
            </div>
          </Field>

          <Field label={tr("Fecha y hora")} testId="edit-booking-when">
            <div className="grid grid-cols-3 gap-2">
              <Input type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} className="bg-background border-border col-span-3 sm:col-span-1" data-testid="edit-booking-date-input" />
              <div><p className="text-[11px] text-muted-foreground mb-1">{tr("Hora de inicio")}</p><Input type="time" step={300} value={form.start} onChange={(e) => e.target.value && setStart(e.target.value)} className="bg-background border-border" data-testid="edit-booking-start-input" /></div>
              <div><p className="text-[11px] text-muted-foreground mb-1">{tr("Hora de fin")}</p><Input type="time" step={300} value={form.end} disabled={fullyPaid} onChange={(e) => e.target.value && setEnd(e.target.value)} className="bg-background border-border" data-testid="edit-booking-end-input" /></div>
            </div>
          </Field>

          {manager && Array.isArray(barbers) && barbers.length > 0 && !paid && (
            <Field label={tr("Barbero")} testId="edit-booking-barber">
              <select value={form.barber_id} onChange={(e) => set({ barber_id: e.target.value })} className="w-full h-10 rounded-md bg-background border border-border px-3 text-sm" data-testid="edit-booking-barber-select">
                {barbers.map((b) => <option key={b.id} value={b.id}>{b.name || b.username}</option>)}
              </select>
            </Field>
          )}

          <Field label={tr("Notas")} testId="edit-booking-notes">
            <Textarea value={form.note} onChange={(e) => set({ note: e.target.value })} rows={2} placeholder={tr("Nota interna (opcional)…")} className="bg-background border-border" data-testid="edit-booking-note-input" />
          </Field>

          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={() => onCancelBooking(booking)} disabled={saving} className="h-11 rounded-full border-destructive/50 text-destructive hover:bg-destructive/10 gap-1.5" data-testid="edit-booking-cancel-booking">
              <Trash2 className="w-4 h-4" />{tr("Eliminar / cancelar")}</Button>
            <Button onClick={() => save(false)} disabled={!dirty || saving} className="flex-1 h-11 rounded-full gap-1.5" data-testid="edit-booking-save">
              <Save className="w-4 h-4" />{saving ? tr("Guardando…") : tr("Guardar")}</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
    <ScheduleConflictDialog open={conflictOpen} busy={saving} rules={conflictRulesList} verb="mover" onContinue={() => save(true)} onBack={() => setConflictOpen(false)} />
  </>);
};

export default EditBookingSheet;
